import * as z from "zod";
import type { DnaConsent } from "../models/Dna.d.ts";

import { allAccessibilityRequirements } from "../_const/dna/AccessibilityRequirement.ts";
import { allFoodPreferences } from "../_const/dna/FoodPreference.ts";
import { allHowDoYouLikeToExplore } from "../_const/dna/HowDoYouLikeToExplore.ts";
import { allHowDoYouLikeToTravel } from "../_const/dna/HowDoYouLikeToTravel.ts";
import { allSplurgePriorities } from "../_const/dna/SplurgePriority.ts";
import { allWhatMakesGreatTripForYou } from "../_const/dna/WhatMakesGreatTripForYou.ts";
import { allWhatShouldAvoidWhenPlanning } from "../_const/dna/WhatShouldAvoidWhenPlanning.ts";
import { allWhatTypeOfPlaceDoYouPrefer } from "../_const/dna/WhatTypeOfPlaceDoYouPrefer.ts";
import { allWhatsYourMinimumHotelStandard } from "../_const/dna/WhatsYourMinimumHotelStandard.ts";
import { allWhereDoYouFeelMostAtHome } from "../_const/dna/WhereDoYouFeelMostAtHome.ts";
import { allWhereDoYouPreferToBeBased } from "../_const/dna/WhereDoYouPreferToBeBased.ts";
import { allYourApproachToFoodWhenYouTravel } from "../_const/dna/YourApproachToFoodWhenYouTravel.ts";

export const dnaSchema = z.object({
  how_do_you_like_to_travel: z.enum(allHowDoYouLikeToTravel),
  what_type_of_place_do_you_prefer: z.enum(allWhatTypeOfPlaceDoYouPrefer),
  where_do_you_feel_most_at_home: z
    .array(z.enum(allWhereDoYouFeelMostAtHome))
    .min(1)
    .max(2),
  your_approach_to_food_when_you_travel: z.enum(
    allYourApproachToFoodWhenYouTravel,
  ),
  how_do_you_like_to_explore: z.enum(allHowDoYouLikeToExplore),
  how_much_do_you_like_a_plan: z.number().int().min(1).max(4),
  spending_preference: z.number().int().min(1).max(5),
  what_do_you_splurge_on: z.array(z.enum(allSplurgePriorities)).max(3),
  what_makes_great_trip_for_you: z
    .array(z.enum(allWhatMakesGreatTripForYou))
    .min(1, {
      message: "Choose at least one interest to continue.",
    }),
  what_makes_great_trip_for_you_details: z
    .record(z.string(), z.array(z.string()))
    .optional(),
  what_makes_great_trip_for_you_custom: z.string().max(1000).optional()
    .nullable(),
  what_should_avoid_when_planning: z
    .array(z.enum(allWhatShouldAvoidWhenPlanning))
    .optional(),
  what_should_avoid_when_planning_details: z
    .record(z.string(), z.array(z.string()))
    .optional(),
  what_should_avoid_when_planning_custom: z
    .string()
    .max(1000)
    .optional()
    .nullable(),

  // Legacy fields remain valid while old saved data and settings screens exist.
  whats_your_minimum_hotel_standard: z
    .enum(allWhatsYourMinimumHotelStandard)
    .optional(),
  where_do_you_prefer_to_be_based: z
    .enum(allWhereDoYouPreferToBeBased)
    .optional(),
});

export const criticalRequirementsSchema = z.object({
  food_preferences: z.array(z.enum(allFoodPreferences)).optional(),
  food_allergy_details: z.string().max(1000).optional().nullable(),
  food_preferences_custom: z.string().max(1000).optional().nullable(),
  accessibility_requirements: z
    .array(z.enum(allAccessibilityRequirements))
    .optional(),
  accessibility_requirements_custom: z
    .string()
    .max(2000)
    .optional()
    .nullable(),
  accessibility_consent_given_at: z.string().datetime().optional().nullable(),
  accessibility_consent_policy_version: z
    .string()
    .max(100)
    .optional()
    .nullable(),
  anything_else_journi_should_know: z
    .string()
    .max(3000)
    .optional()
    .nullable(),
});

export type UpsertDnaRequest = {
  p_user_id: string;
  p_patch: Partial<z.infer<typeof dnaSchema>>;
};

export type UpsertCriticalRequirementsRequest = {
  p_user_id: string;
  p_patch: Partial<z.infer<typeof criticalRequirementsSchema>>;
  p_consent?: Pick<DnaConsent, 'page' | 'version' | 'status'>;
};

// Structured Outputs cannot return objects with arbitrary keys.
// Preserve detailed and user-entered values by merging the AI proposal
// into the existing DNA after the response has been validated.
const refinementDnaSchema = dnaSchema
  .omit({
    what_makes_great_trip_for_you_details: true,
    what_makes_great_trip_for_you_custom: true,

    what_should_avoid_when_planning: true,
    what_should_avoid_when_planning_details: true,
    what_should_avoid_when_planning_custom: true,

    whats_your_minimum_hotel_standard: true,
    where_do_you_prefer_to_be_based: true,
  })
  .extend({
    what_should_avoid_when_planning: z
      .array(z.enum(allWhatShouldAvoidWhenPlanning))
      .nullable(),
  });

export const refinementSuggestionSchema = z.object({
  refinements: refinementDnaSchema.nullable(),
  refinement_ids: z.array(z.string()),
  refinement_comment: z.string().nullable(),
});

export type RefinementSuggestion = z.infer<
  typeof refinementSuggestionSchema
>;
